// client/src/components/pages/LexiStar/NotificationRules/RuleBuilder.tsx (converted from .jsx, ST-00282)
//
// The rule builder (LS-09 §5.16; FR-NTF-001/002/003/005/006/007) in the
// natural-language shape of the spec:
//   When ⟨event types / categories / severities⟩ and ⟨conditions⟩,
//   notify ⟨recipients⟩ via ⟨channels, in fallback order⟩ using ⟨template⟩
//   (live preview per language); limit ⟨dedupe, per-rule throttle, quiet hours,
//   digest, severity floor⟩; escalate to ⟨…⟩ after ⟨…⟩.
// The sentence the rule reads as is shown live. Saving validates inline with
// the API's limits. An existing rule can be tested against a real event
// (POST /notification-rules/:id:test — rendered and sent to the caller only).
//
// ST-00282: a rule the server marks `locked` (the seeded no-go rule: a no-go zone always alerts)
// opens with the server's reason at the top; its Active box stays ticked, quiet hours and a digest
// cannot be switched on, the per-rule limit is off (not applied to it), and lockErrors says before
// saving what would stop it alerting on every no-go entry (its types, categories or severities
// leaving no-go entries out, a condition, repeats ignored for more than 15 minutes) — the server
// refuses the same (422 NOGO_RULE_LOCKED). Everything else stays editable, including a wider
// "When".

import { useEffect, useMemo, useState, type ComponentType, type ReactNode } from 'react';
import { FaLock, FaPlus, FaTimes, FaVial } from 'react-icons/fa';
import ThemedSelect from '../../../common/ThemedSelect';
import ThemedTimePicker from '../../../common/ThemedTimePicker';
import { inputClasses } from '../StatusBoard/atoms';

import RecipientsEditorJs from './RecipientsEditor';
import ChannelOrderJs from './ChannelOrder';
import TemplatePreviewJs from './TemplatePreview';
import {
  createRule as createRuleJs,
  updateRule as updateRuleJs,
  testRule as testRuleJs,
} from './notificationsApi';
import {
  EVENT_TYPE_OPTIONS,
  CATEGORIES,
  SEVERITIES,
  CONDITION_OPS,
  CONDITION_PATHS,
  RULE_CHANNELS,
  newRule,
  fromRule,
  toPayload,
  validateRule,
  lockErrors,
  describeRule,
  sampleVars,
  type Option,
  type RuleModel,
} from './ruleLogic';
import type {
  NotificationRulePayload,
  NotificationRuleRow,
  RuleCondition,
  RuleEscalationTier,
  RuleRecipient,
  RuleThrottle,
} from '../../../../types/lexistar-notification-rules';

/** ST-00282: what must hold for a rule, and why (the server's `locked`). */
export interface RuleLock {
  code: string;
  reason: string;
  /** Controls kept fixed: 'active' (ticked), 'quiet_hours' and 'digest' (off). */
  fields: string[];
  /** The most seconds per_rule_s / dedupe_s may be. */
  max_throttle_s?: number;
}

/** A rule row as the builder opens it. */
export type BuilderRule = NotificationRuleRow & { locked?: RuleLock | null };

/** A recipient group (the "a group" recipient picker, describeRule). */
export interface BuilderGroup {
  id: string;
  name?: string | null;
  [column: string]: unknown;
}

type Row = Record<string, unknown>;

/** POST /notification-rules/:id:test answer, as far as the builder reads it. */
interface TestRuleResult {
  enqueued?: unknown[];
  matches_filter?: boolean;
}

/** An axios error body, as far as apiError reads it. */
interface ApiErrorBody {
  error?: string;
  message?: string;
  details?: Array<string | { field?: string; message?: string }>;
}

// Typed views of the JavaScript modules this component uses.
const RecipientsEditor = RecipientsEditorJs as unknown as ComponentType<{
  value: RuleRecipient[];
  onChange: (value: RuleRecipient[]) => void;
  kinds?: string[];
  groups: readonly BuilderGroup[];
  users: readonly Row[];
  idPrefix?: string;
  disabled?: boolean;
  label?: string;
}>;
const ChannelOrder = ChannelOrderJs as unknown as ComponentType<{
  value: string[];
  onChange: (value: string[]) => void;
  options?: Option[];
  label?: string;
  disabled?: boolean;
}>;
const TemplatePreview = TemplatePreviewJs as unknown as ComponentType<{
  templateKey: string | null | undefined;
  channel?: string;
  vars?: Row | ((lang: string) => Row);
  body?: string;
  buttons?: unknown;
  debounceMs?: number;
  className?: string;
}>;
const createRule = createRuleJs as unknown as (
  data: NotificationRulePayload
) => Promise<{ rule?: BuilderRule } | BuilderRule | null | undefined>;
const updateRule = updateRuleJs as unknown as (
  id: string,
  data: NotificationRulePayload
) => Promise<{ rule?: BuilderRule } | BuilderRule | null | undefined>;
const testRule = testRuleJs as unknown as (
  id: string,
  body: { event_id: string }
) => Promise<TestRuleResult | null | undefined>;

// The shared input classes carry w-full; fixed-width fields drop it.
const fixedWidthInput = inputClasses.replace('w-full ', '');

const apiError = (err: unknown, fallback: string): string => {
  const e = err as { response?: { data?: ApiErrorBody }; message?: string } | null | undefined;
  const data: ApiErrorBody = e?.response?.data || {};
  const details = Array.isArray(data.details)
    ? data.details.map((d) =>
        typeof d === 'string' ? d : `${d.field ? `${d.field}: ` : ''}${d.message}`
      )
    : [];
  return (
    [data.error || data.message, ...details].filter(Boolean).join(' — ') || e?.message || fallback
  );
};

function Clause({ word, children }: { word: string; children?: ReactNode }) {
  return (
    <section
      className="grid grid-cols-1 md:grid-cols-[7rem_1fr] gap-2 md:gap-4 py-4 border-b border-slate-100"
      aria-label={word}
    >
      <h4 className="text-sm font-bold text-slate-900 md:pt-2">{word}</h4>
      <div className="min-w-0 space-y-3">{children}</div>
    </section>
  );
}

const minutes = (s: unknown): number | '' => (Number(s) > 0 ? Math.round(Number(s) / 60) : '');
const seconds = (m: string | null): number =>
  m === '' || m === null ? 0 : Math.max(0, Math.round(Number(m) * 60));

/** A ThemedSelect change's value as one string / a list of strings. */
const one = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
const list = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);

/** The rule saved (the API answers { rule } or the row). */
const savedRule = (
  res: { rule?: BuilderRule } | BuilderRule | null | undefined
): BuilderRule | null | undefined =>
  (res as { rule?: BuilderRule } | null | undefined)?.rule || (res as BuilderRule | null | undefined);

export interface RuleBuilderProps {
  isOpen: boolean;
  /** The rule to edit; null = a new rule. */
  rule: BuilderRule | null | undefined;
  templates: readonly Row[];
  groups: readonly BuilderGroup[];
  users: readonly Row[];
  canManage: boolean;
  onClose: () => void;
  onSaved?: (saved: BuilderRule | null | undefined) => void;
}

export default function RuleBuilder({
  isOpen,
  rule,
  templates,
  groups,
  users,
  canManage,
  onClose,
  onSaved,
}: RuleBuilderProps) {
  const [model, setModel] = useState<RuleModel>(newRule());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [testEvent, setTestEvent] = useState('');
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setModel(rule ? fromRule(rule) : newRule());
    setError(null);
    setShowErrors(false);
    setTestEvent('');
    setTestResult(null);
  }, [isOpen, rule]);

  const errors = useMemo(
    () => [...validateRule(model), ...lockErrors(model, rule?.locked)],
    [model, rule]
  );
  const groupsById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);
  const templateKeys = useMemo(
    () =>
      [
        ...new Set(templates.map((t) => t.key).filter((k): k is string => typeof k === 'string')),
      ].sort(),
    [templates]
  );
  const firstType =
    model.event_filter.types.find((t) => !t.endsWith('*')) || 'ROUTE.DEVIATION_OPENED';
  const previewChannel = model.channels[0] || 'in_app';

  if (!isOpen) return null;

  // ST-00282: what must hold for this rule (the no-go rule always alerts). The filter stays
  // editable: lockErrors (and the server) refuse only what would stop it alerting.
  const lock = rule?.locked || null;
  const lockedFields = lock ? lock.fields || ['active', 'quiet_hours', 'digest', 'per_rule_s'] : [];
  const activeLocked = lockedFields.includes('active');
  const quietLocked = lockedFields.includes('quiet_hours');
  const digestLocked = lockedFields.includes('digest');
  const perRuleLocked = lockedFields.includes('per_rule_s');
  const canEditFilter = canManage;

  const set = (patch: Partial<RuleModel>) => setModel((m) => ({ ...m, ...patch }));
  const setFilter = (patch: Partial<RuleModel['event_filter']>) =>
    setModel((m) => ({ ...m, event_filter: { ...m.event_filter, ...patch } }));
  const setThrottle = (patch: Partial<RuleThrottle>) =>
    setModel((m) => ({ ...m, throttle: { ...m.throttle, ...patch } }));
  const setTier = (i: number, patch: Partial<RuleEscalationTier>) =>
    setModel((m) => ({
      ...m,
      escalation: m.escalation.map((t, j) => (j === i ? { ...t, ...patch } : t)),
    }));
  const setCondition = (i: number, patch: Partial<RuleCondition>) =>
    setFilter({
      conditions: model.event_filter.conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)),
    });

  const save = async () => {
    setShowErrors(true);
    if (errors.length) return;
    setSaving(true);
    setError(null);
    try {
      const payload = toPayload(model);
      const res = model.id ? await updateRule(model.id, payload) : await createRule(payload);
      onSaved?.(savedRule(res));
      onClose();
    } catch (err) {
      setError(apiError(err, 'The rule was not saved'));
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTestResult(null);
    try {
      const res = await testRule(String(model.id), { event_id: testEvent.trim() });
      const n = Array.isArray(res?.enqueued) ? res.enqueued.length : null;
      const note =
        res?.matches_filter === false
          ? " (the event does not match this rule's filter — sent anyway so you can see it)"
          : '';
      setTestResult({
        ok: true,
        text: `Test sent to you${n !== null ? ` (${n} message${n === 1 ? '' : 's'})` : ''}${note}. Check your notifications.`,
      });
    } catch (err) {
      setTestResult({ ok: false, text: apiError(err, 'The test failed') });
    }
  };

  const multi = (
    id: string,
    label: string,
    value: string[],
    onChange: (v: string[]) => void,
    options: Option[]
  ) => (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-slate-600 mb-1">
        {label}
      </label>
      <ThemedSelect
        id={id}
        isMulti
        value={value}
        onChange={(e) => onChange(list(e.target.value))}
        className={inputClasses}
        disabled={!canEditFilter}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </ThemedSelect>
    </div>
  );

  const quiet = model.throttle.quiet_hours;
  const digest = model.throttle.digest;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={model.id ? `Rule ${model.name}` : 'New notification rule'}
        className="bg-surface rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[94vh] flex flex-col overflow-hidden"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0 space-y-2">
            <label htmlFor="rule-name" className="sr-only">
              Rule name
            </label>
            <input
              id="rule-name"
              value={model.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="Name this rule"
              className="w-full text-lg font-bold text-slate-900 bg-transparent border-b border-transparent focus:border-primary-500 focus:outline-none"
              disabled={!canManage}
            />
            <p className="text-sm text-slate-600" aria-live="polite" data-testid="rule-sentence">
              {describeRule(model, { groupsById })}
            </p>
            {lock && (
              <p
                data-testid="rule-locked-notice"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-800"
              >
                <FaLock size={10} aria-hidden="true" />
                {lock.reason}
              </p>
            )}
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 whitespace-nowrap">
            <input
              type="checkbox"
              checked={activeLocked ? true : model.active}
              onChange={(e) => set({ active: e.target.checked })}
              disabled={!canManage || activeLocked}
              className="rounded border-slate-300"
            />
            Active
          </label>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600"
          >
            <FaTimes size={18} />
          </button>
        </div>

        <div className="px-6 overflow-y-auto">
          <Clause word="When">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <div className="lg:col-span-3">
                {multi(
                  'rule-types',
                  'Event types',
                  model.event_filter.types,
                  (v) => setFilter({ types: v }),
                  EVENT_TYPE_OPTIONS
                )}
              </div>
              {multi(
                'rule-categories',
                'or whole categories',
                model.event_filter.categories,
                (v) => setFilter({ categories: v }),
                CATEGORIES.map((c) => ({ value: c, label: c }))
              )}
              {multi(
                'rule-severities',
                'Severities',
                model.event_filter.severities,
                (v) => setFilter({ severities: v }),
                SEVERITIES
              )}
            </div>
            <div className="space-y-2" role="group" aria-label="Conditions">
              {model.event_filter.conditions.map((c, i) => (
                <div key={i} className="flex flex-col sm:flex-row gap-2">
                  <input
                    aria-label={`Condition ${i + 1} field`}
                    value={c.path}
                    onChange={(e) => setCondition(i, { path: e.target.value })}
                    placeholder="payload.off_route_km"
                    list="rule-condition-paths"
                    className={`${inputClasses} sm:w-56 font-mono text-xs`}
                    disabled={!canEditFilter}
                  />
                  <div className="sm:w-40">
                    <ThemedSelect
                      aria-label={`Condition ${i + 1} operator`}
                      value={c.op}
                      onChange={(e) => setCondition(i, { op: one(e.target.value) })}
                      className={inputClasses}
                      disabled={!canEditFilter}
                    >
                      {CONDITION_OPS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </ThemedSelect>
                  </div>
                  {!['exists', 'not_exists'].includes(c.op) && (
                    <input
                      aria-label={`Condition ${i + 1} value`}
                      value={Array.isArray(c.value) ? c.value.join(', ') : String(c.value ?? '')}
                      onChange={(e) => setCondition(i, { value: e.target.value })}
                      placeholder={['in', 'not_in'].includes(c.op) ? 'a, b, c' : 'value'}
                      className={`${inputClasses} flex-1`}
                      disabled={!canEditFilter}
                    />
                  )}
                  {canEditFilter && (
                    <button
                      type="button"
                      onClick={() =>
                        setFilter({
                          conditions: model.event_filter.conditions.filter((_, j) => j !== i),
                        })
                      }
                      aria-label={`Remove condition ${i + 1}`}
                      className="p-2 text-slate-400 hover:text-red-600"
                    >
                      <FaTimes size={12} />
                    </button>
                  )}
                </div>
              ))}
              <datalist id="rule-condition-paths">
                {CONDITION_PATHS.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
              {canEditFilter && (
                <button
                  type="button"
                  onClick={() =>
                    setFilter({
                      conditions: [
                        ...model.event_filter.conditions,
                        { path: '', op: 'gt', value: '' },
                      ],
                    })
                  }
                  className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:underline"
                >
                  <FaPlus size={9} /> Add condition
                </button>
              )}
            </div>
          </Clause>

          <Clause word="Notify">
            <RecipientsEditor
              value={model.recipients}
              onChange={(v) => set({ recipients: v })}
              groups={groups}
              users={users}
              idPrefix="rule-rcp"
              disabled={!canManage}
            />
          </Clause>

          <Clause word="Via">
            <ChannelOrder
              value={model.channels}
              onChange={(v) => set({ channels: v })}
              disabled={!canManage}
            />
            <p className="text-xs text-slate-500">
              Tried in this order; when one fails (or the driver has not opted in), the next is
              used.
            </p>
          </Clause>

          <Clause word="Using">
            <div className="sm:w-80">
              <ThemedSelect
                aria-label="Template"
                value={model.template_key}
                onChange={(e) => set({ template_key: one(e.target.value) })}
                className={inputClasses}
                disabled={!canManage}
              >
                {model.template_key && !templateKeys.includes(model.template_key) && (
                  <option value={model.template_key}>{model.template_key}</option>
                )}
                {templateKeys.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </ThemedSelect>
            </div>
            <TemplatePreview
              templateKey={model.template_key}
              channel={previewChannel}
              vars={(lang) => sampleVars(firstType, lang)}
            />
          </Clause>

          <Clause word="Limit">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <label
                  htmlFor="rule-dedupe"
                  className="block text-xs font-medium text-slate-600 mb-1"
                >
                  Ignore repeats for the same truck (min)
                </label>
                <input
                  id="rule-dedupe"
                  type="number"
                  min="0"
                  placeholder="off"
                  value={minutes(model.throttle.dedupe_s)}
                  onChange={(e) => setThrottle({ dedupe_s: seconds(e.target.value) })}
                  className={inputClasses}
                  disabled={!canManage}
                />
              </div>
              <div>
                <label
                  htmlFor="rule-per-rule"
                  className="block text-xs font-medium text-slate-600 mb-1"
                >
                  At most one message per (min)
                </label>
                <input
                  id="rule-per-rule"
                  type="number"
                  min="0"
                  placeholder="off"
                  value={perRuleLocked ? '' : minutes(model.throttle.per_rule_s)}
                  onChange={(e) => setThrottle({ per_rule_s: seconds(e.target.value) })}
                  className={inputClasses}
                  disabled={!canManage || perRuleLocked}
                  title={perRuleLocked ? 'Not applied to this rule: every no-go entry is sent.' : undefined}
                />
              </div>
              <div>
                <label
                  htmlFor="rule-floor"
                  className="block text-xs font-medium text-slate-600 mb-1"
                >
                  Severity floor
                </label>
                <ThemedSelect
                  id="rule-floor"
                  value={
                    typeof model.throttle.severity_floor === 'string'
                      ? model.throttle.severity_floor
                      : ''
                  }
                  onChange={(e) => setThrottle({ severity_floor: one(e.target.value) || null })}
                  className={inputClasses}
                  isClearable
                  disabled={!canManage}
                >
                  <option value="">No floor</option>
                  {SEVERITIES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label} and up
                    </option>
                  ))}
                </ThemedSelect>
              </div>
            </div>
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 space-y-2">
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={!!quiet}
                    onChange={(e) =>
                      setThrottle({
                        quiet_hours: e.target.checked
                          ? { from: '22:00', to: '05:00', tz: 'Africa/Johannesburg' }
                          : null,
                      })
                    }
                    disabled={!canManage || (quietLocked && !quiet)}
                    className="rounded border-slate-300"
                  />
                  Quiet hours (held for the digest, SAST)
                </label>
                {quiet && (
                  <div className="flex items-center gap-2">
                    <ThemedTimePicker
                      value={quiet.from}
                      onChange={(e) =>
                        setThrottle({ quiet_hours: { ...quiet, from: e.target.value } })
                      }
                      disabled={!canManage}
                    />
                    <span className="text-sm text-slate-500">to</span>
                    <ThemedTimePicker
                      value={quiet.to}
                      onChange={(e) =>
                        setThrottle({ quiet_hours: { ...quiet, to: e.target.value } })
                      }
                      disabled={!canManage}
                    />
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={!!digest}
                    onChange={(e) =>
                      setThrottle({ digest: e.target.checked ? { every_s: 3600 } : null })
                    }
                    disabled={!canManage || (digestLocked && !digest)}
                    className="rounded border-slate-300"
                  />
                  Digest instead of single messages
                </label>
                {digest && (
                  <div className="flex items-center gap-2">
                    <label htmlFor="rule-digest" className="text-sm text-slate-600">
                      every
                    </label>
                    <input
                      id="rule-digest"
                      type="number"
                      min="1"
                      value={minutes(digest.every_s)}
                      onChange={(e) =>
                        setThrottle({ digest: { every_s: seconds(e.target.value) } })
                      }
                      className={`${fixedWidthInput} w-24`}
                      disabled={!canManage}
                    />
                    <span className="text-sm text-slate-600">min</span>
                  </div>
                )}
              </div>
            </div>
          </Clause>

          <Clause word="Escalate">
            {model.escalation.length === 0 && (
              <p className="text-sm text-slate-500">
                No escalation — the first message is the only one.
              </p>
            )}
            {model.escalation.map((tier, i) => (
              <div
                key={i}
                className="rounded-lg border border-slate-200 p-3 space-y-3"
                role="group"
                aria-label={`Escalation tier ${i + 1}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-800">
                    Tier {i + 1}: if still open after
                  </span>
                  <input
                    aria-label={`Tier ${i + 1} minutes`}
                    type="number"
                    min="1"
                    value={minutes(tier.after_s)}
                    onChange={(e) => setTier(i, { after_s: seconds(e.target.value) })}
                    className={`${fixedWidthInput} w-20`}
                    disabled={!canManage}
                  />
                  <span className="text-sm text-slate-600">min</span>
                  {canManage && (
                    <button
                      type="button"
                      onClick={() =>
                        set({ escalation: model.escalation.filter((_, j) => j !== i) })
                      }
                      aria-label={`Remove tier ${i + 1}`}
                      className="ml-auto p-1 text-slate-400 hover:text-red-600"
                    >
                      <FaTimes size={12} />
                    </button>
                  )}
                </div>
                <RecipientsEditor
                  value={tier.recipients || []}
                  onChange={(v) => setTier(i, { recipients: v })}
                  groups={groups}
                  users={users}
                  idPrefix={`tier-${i}`}
                  label={`Tier ${i + 1} recipients`}
                  disabled={!canManage}
                />
                <ChannelOrder
                  value={tier.channels || []}
                  onChange={(v) => setTier(i, { channels: v })}
                  options={RULE_CHANNELS}
                  label={`Tier ${i + 1} channels`}
                  disabled={!canManage}
                />
              </div>
            ))}
            {canManage && model.escalation.length < 5 && (
              <button
                type="button"
                onClick={() => {
                  const last = model.escalation[model.escalation.length - 1];
                  set({
                    escalation: [
                      ...model.escalation,
                      {
                        after_s: last ? Number(last.after_s) + 600 : 600,
                        recipients: [{ kind: 'role', ref: 'lexistar.deviations.approve' }],
                        channels: ['telegram', 'in_app'],
                      },
                    ],
                  });
                }}
                className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:underline"
              >
                <FaPlus size={9} /> Add escalation tier
              </button>
            )}
          </Clause>

          {model.id && canManage && (
            <Clause word="Test">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  aria-label="Event id to test with"
                  value={testEvent}
                  onChange={(e) => setTestEvent(e.target.value)}
                  placeholder="Event id (32 hex characters, from Events or Replay)"
                  className={`${inputClasses} font-mono text-xs flex-1`}
                />
                <button
                  type="button"
                  onClick={runTest}
                  disabled={!/^[0-9a-f]{32}$/.test(testEvent.trim())}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                >
                  <FaVial size={11} /> Send test to me
                </button>
              </div>
              {testResult && (
                <p
                  role="status"
                  className={`text-sm ${testResult.ok ? 'text-emerald-700' : 'text-red-700'}`}
                >
                  {testResult.text}
                </p>
              )}
            </Clause>
          )}
        </div>

        <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 space-y-2">
          {showErrors && errors.length > 0 && (
            <div role="alert">
              <ul className="text-sm text-red-700 list-disc pl-5" aria-label="Rule problems">
                {errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </div>
          )}
          {error && (
            <p role="alert" className="text-sm text-red-700">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              {canManage ? 'Cancel' : 'Close'}
            </button>
            {canManage && (
              <button
                onClick={save}
                disabled={saving}
                className="px-5 py-2 text-sm font-semibold text-white rounded-lg shadow-sm bg-primary-500 hover:bg-primary-600 disabled:bg-slate-400"
              >
                {saving ? 'Saving...' : model.id ? 'Save rule' : 'Create rule'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
